/** Cognito group for OCP Request only (separate from Booking Request BR/BS). */
export const OCP_COGNITO_GROUPS = ['OC'];

/** OCP request lifecycle statuses (align with cargo-track-ws / CargoTrackConstants). */
export const OCP_STATUS = {
  SAVED: 'SAVED',
  SUBMITTED: 'SUBMITTED',
  PROCESSING: 'PROCESSING',
  APPROVED: 'MATSON APPROVED',
  CANCELLED: 'CANCEL',
  REJECTED: 'MATSON REJECTED',
};

export const OCP_TARIFF_EXEMPT = '9991A';
export const OCP_SAVE_ACTION_SAVE = 'SAVE';
export const OCP_SAVE_ACTION_SUBMIT = 'SUBMIT';

export const ocpRequestStatusList = [
  { title: 'Select', value: '' },
  { title: 'SAVED', value: OCP_STATUS.SAVED },
  { title: 'SUBMITTED', value: OCP_STATUS.SUBMITTED },
  { title: 'PROCESSING', value: OCP_STATUS.PROCESSING },
  { title: 'MATSON APPROVED', value: OCP_STATUS.APPROVED },
  { title: 'CANCEL', value: OCP_STATUS.CANCELLED },
  { title: 'MATSON REJECTED', value: OCP_STATUS.REJECTED },
];

const OCP_READ_ONLY_STATUSES = new Set([
  OCP_STATUS.SUBMITTED,
  OCP_STATUS.PROCESSING,
  OCP_STATUS.APPROVED,
  OCP_STATUS.CANCELLED,
]);

const OCP_CANCELLABLE_STATUSES = new Set([
  OCP_STATUS.SAVED,
  OCP_STATUS.SUBMITTED,
  OCP_STATUS.PROCESSING,
  OCP_STATUS.APPROVED,
]);

export function hasOcpCognitoAccess(groups) {
  if (!Array.isArray(groups)) {
    return false;
  }
  return OCP_COGNITO_GROUPS.some((role) => groups.includes(role));
}

export function parseVvdFromRow(row) {
  if (!row) {
    return { vesselCode: '', voyage: '', directionSeq: '' };
  }
  if (row.vesselCode) {
    return {
      vesselCode: String(row.vesselCode).trim(),
      voyage: row.voyage != null ? String(row.voyage).trim() : '',
      directionSeq:
        row.directionSeq != null
          ? String(row.directionSeq).trim()
          : row.direction != null
          ? String(row.direction).trim()
          : '',
    };
  }
  const vvd = String(row.vvd || '').replace(/\s/g, '');
  return {
    vesselCode: vvd.substring(0, 3),
    voyage: vvd.length >= 6 ? vvd.substring(3, 6) : vvd.substring(3),
    directionSeq: vvd.length >= 7 ? vvd.substring(6, 7) : '',
  };
}

export function formatVvdDisplay(vvd) {
  const parts = parseVvdFromRow(vvd);
  return [parts.vesselCode, parts.voyage, parts.directionSeq]
    .filter(Boolean)
    .join(' ');
}

export function hasOcpRequestId(ocpRequestId) {
  return ocpRequestId !== null && ocpRequestId !== undefined && ocpRequestId !== '';
}

export function normalizeOcpStatus(status) {
  if (status === null || status === undefined) {
    return '';
  }
  return String(status).trim();
}

export function isOcpCancelled(status) {
  return normalizeOcpStatus(status) === OCP_STATUS.CANCELLED;
}

export function isOcpRejected(status) {
  return normalizeOcpStatus(status) === OCP_STATUS.REJECTED;
}

/** Active = blocks a new OCP until cancelled or rejected. */
export function isActiveOcpStatus(status) {
  const normalized = normalizeOcpStatus(status);
  if (!normalized) {
    return false;
  }
  return !isOcpCancelled(normalized) && !isOcpRejected(normalized);
}

export function isOcpReadOnly(status) {
  const normalized = normalizeOcpStatus(status);
  if (!normalized) {
    return false;
  }
  return OCP_READ_ONLY_STATUSES.has(normalized);
}

export function canEditOcpForm(status) {
  const normalized = normalizeOcpStatus(status);
  return !normalized || normalized === OCP_STATUS.SAVED;
}

export function canCancelOcp(status) {
  const normalized = normalizeOcpStatus(status);
  return OCP_CANCELLABLE_STATUSES.has(normalized);
}

export function canAddOcpForRow(row) {
  if (!row) {
    return true;
  }
  if (!hasOcpRequestId(row.ocpRequestId)) {
    return true;
  }
  return isOcpCancelled(row.ocpStatus) || isOcpRejected(row.ocpStatus);
}

export function canUpdateOcpRow(row) {
  return Boolean(row && hasOcpRequestId(row.ocpRequestId));
}

export function isTariffExempt(serviceContract) {
  return (
    String(serviceContract || '').trim().toUpperCase() === OCP_TARIFF_EXEMPT
  );
}

export function getOcpActionState(ocpStatus, isUserAssumed) {
  if (isUserAssumed) {
    return { saveEnabled: false, submitEnabled: false, cancelEnabled: false };
  }
  return {
    saveEnabled: canEditOcpForm(ocpStatus),
    submitEnabled: canEditOcpForm(ocpStatus),
    cancelEnabled: canCancelOcp(ocpStatus),
  };
}

export function toSelectOptions(items, valueKey, titleFn) {
  const list = [{ title: 'Select', value: '' }];
  (items || []).forEach((item) => {
    list.push({
      title: titleFn(item),
      value: String(item[valueKey]),
      raw: item,
    });
  });
  return list;
}

export function normalizeShipmentNumberInput(value) {
  let shipmentNumber = String(value || '').trim();
  if (shipmentNumber.length > 7) {
    if (shipmentNumber.startsWith('MATS') && shipmentNumber.length > 10) {
      shipmentNumber = shipmentNumber.substring(4, 11);
    } else {
      shipmentNumber = shipmentNumber.substring(0, 7);
    }
  }
  return shipmentNumber;
}
